// The one place that decides whether the realtime gateway is configured, and
// the one place that says what it means when it is not — BUG-1454.
//
// WHY A MODULE FOR ONE STRING. Three call sites need the same answer in the
// same words: the browser-side inert subscriber (`create-subscriber.ts`) and
// each app's boot check (`apps/*/src/instrumentation.ts`). Three hand-written
// copies is how one of them ends up naming a variable that was renamed two
// quarters ago. ADR 043 — GCP Observability also records that a message string
// a log-based alert keys on becomes load-bearing and must not be reworded
// casually, which is another reason for there to be exactly one of it.
//
// DELIBERATELY IMPORT-FREE. It is read by the browser AND by the Node
// instrumentation hook, so it can carry neither `client-only` nor `server-only`
// — and nothing it imports may either. Keep it that way.

/** The environment variable that carries the socket.io gateway origin. */
export const REALTIME_GATEWAY_ENV_VAR = 'NEXT_PUBLIC_REALTIME_GATEWAY_URL';

/**
 * What to say when {@link REALTIME_GATEWAY_ENV_VAR} is absent.
 *
 * Three things, in this order, because that is the order the reader needs them:
 * what is broken, what is NOT broken, and what to do. The middle one is not
 * padding — a missing gateway is degraded-but-correct (ADR 011 — Durable
 * Events: the cursored catch-up read is the correctness floor, broadcasts are
 * best-effort hints), so this must not read as data loss and must not be
 * escalated into a user-facing error.
 *
 * The fix line names `--public-only` on purpose: the developer reading this is,
 * by construction, someone whose `.env.local` predates the value being added to
 * the public tier — quite possibly because their GCP login lapsed and the full
 * pull has been refusing ever since.
 */
export const REALTIME_GATEWAY_MISSING_NOTICE =
    `[realtime] ${REALTIME_GATEWAY_ENV_VAR} is not set — LIVE UPDATES ARE DISABLED. ` +
    'Pages still render and catch-up still serves current state on load, so nothing is lost — ' +
    'but nothing will move on its own (bids, lot status, auction pause) until you reload. ' +
    'Fix: run `bash scripts/pull-secrets.sh` (or `bash scripts/pull-secrets.sh --public-only` ' +
    'if your gcloud login has lapsed), then start the gateway with `npm run dev:realtime-gateway`.';

/**
 * Decide whether the gateway is configured.
 *
 * Trim-aware, and that is load-bearing rather than defensive:
 * `createRealtimeSubscriber` decides on `gatewayUrl?.trim()`, so a value of
 * `" "` is already treated as absent there. A helper that disagreed would let a
 * tab go inert while the boot check reported everything fine — the two halves of
 * this fix contradicting each other, which is worse than either being missing.
 *
 * @param gatewayUrl - The raw value of {@link REALTIME_GATEWAY_ENV_VAR}.
 * @returns The notice to emit, or `null` when the gateway is configured.
 * @sideEffect None — the caller decides how to say it.
 */
export function realtimeGatewayMissingNotice(gatewayUrl?: string): string | null {
    return gatewayUrl?.trim() ? null : REALTIME_GATEWAY_MISSING_NOTICE;
}

/** Options for {@link realtimeGatewayBootNotice}. */
export interface RealtimeGatewayBootNoticeOptions {
    /** Raw value of {@link REALTIME_GATEWAY_ENV_VAR}. */
    gatewayUrl?: string;
    /**
     * Raw `process.env.NEXT_RUNTIME` — `'nodejs'` or `'edge'`.
     *
     * Passed IN rather than read here so the decision is a pure function of its
     * arguments and can be tested for both runtimes without mutating the
     * process environment.
     */
    runtime?: string;
}

/**
 * The boot-time variant of {@link realtimeGatewayMissingNotice}: whether an
 * app's `instrumentation.ts` should announce the missing gateway.
 *
 * **Node runtime only, and that is not belt-and-braces.** Next invokes
 * `register()` once per runtime — nodejs AND edge (middleware boot) — so an
 * ungated warn prints twice, and the edge copy comes back through the console
 * patch DOUBLE-ENCODED and re-tagged `severity: ERROR`. A configuration notice
 * arriving in Cloud Logging as an error is how a non-issue pages someone
 * ([ADR 043 — GCP Observability](../../../../docs/adrs/043_GCP_NATIVE_OBSERVABILITY.md)
 * makes `severity=ERROR` an alerting trigger). Observed locally while verifying
 * BUG-1454; tracked in its own right as BUG-1510, which covers the general case
 * for every boot-time log line rather than this one call.
 *
 * The gate lives HERE, not at the two call sites, so it is unit-testable and so
 * the two apps cannot drift on it — the same reason the message itself does
 * ([ADR 049 — Shared Fan-Out](../../../../docs/adrs/049_REALTIME_FANOUT_COMPOSITION_IS_SHARED.md)).
 *
 * @param options - The gateway URL and the current Next runtime.
 * @returns The notice to emit, or `null` (configured, or not the node runtime).
 * @sideEffect None — the caller decides how to say it.
 */
export function realtimeGatewayBootNotice({
    gatewayUrl,
    runtime,
}: RealtimeGatewayBootNoticeOptions): string | null {
    if (runtime !== 'nodejs') return null;
    return realtimeGatewayMissingNotice(gatewayUrl);
}
